import { useState, type FormEvent } from 'react';
import { createApiKey, revokeApiKey, rotateApiKey } from '../api/endpoints';
import type { AccessStage, ApiKeyListItem } from '../api/types';
import { Modal } from './Modal';
import { useToast } from './ToastProvider';
import { formatDate, formatShortDateTime } from '../utils/format';

/**
 * The client's API keys: list, create, rotate, revoke. On Developers → API keys since 2026-10-23
 * (it used to sit at the foot of My Company). Changing keys is ClientAdmin only; the server
 * enforces the same (ApiKeysController). Razor: Account/ApiKeys.
 */
export function ApiKeysCard({
  accessStage,
  apiKeys,
  canManage,
  onChanged,
}: {
  accessStage: AccessStage;
  apiKeys: ApiKeyListItem[];
  canManage: boolean;
  onChanged: () => void;
}) {
  const { showSuccess, showError } = useToast();
  const [showAdd, setShowAdd] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyEnv, setNewKeyEnv] = useState('Test');
  const [busy, setBusy] = useState(false);

  // Live keys need live access: Beta Live or a paid plan (the server enforces the same rule).
  const liveKeysAllowed = accessStage === 'BetaLive' || accessStage === 'Live';

  // The plaintext secret exists only in the response that created it. Held here until the user
  // dismisses it, and deliberately never refetched — the server cannot produce it again.
  const [revealed, setRevealed] = useState<{ name: string; key: string } | null>(null);

  async function handleCreate(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      const result = await createApiKey({ name: newKeyName, environment: newKeyEnv });
      if (result.success && result.fullKey) {
        setRevealed({ name: result.keyName ?? newKeyName, key: result.fullKey });
        setShowAdd(false);
        setNewKeyName('');
        onChanged();
      } else {
        showError(result.error ?? 'Failed to create API key.');
      }
    } catch {
      showError('Failed to create API key.');
    } finally {
      setBusy(false);
    }
  }

  async function handleRotate(key: ApiKeyListItem) {
    setBusy(true);
    try {
      const result = await rotateApiKey(key.id);
      if (result.success && result.fullKey) {
        setRevealed({ name: result.keyName ?? key.name, key: result.fullKey });
        onChanged();
      } else {
        showError(result.error ?? 'Failed to rotate API key.');
      }
    } catch {
      showError('Failed to rotate API key.');
    } finally {
      setBusy(false);
    }
  }

  async function handleRevoke(key: ApiKeyListItem) {
    setBusy(true);
    try {
      const result = await revokeApiKey(key.id);
      if (result.success) {
        showSuccess(`API key "${result.keyName ?? key.name}" has been revoked.`);
        onChanged();
      } else {
        showError(result.error ?? 'Failed to revoke API key.');
      }
    } catch {
      showError('Failed to revoke API key.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h6 className="mb-0">
          <i className="bi bi-key-fill me-2 text-warning" />
          API Keys
        </h6>
        {canManage && (
          <button className="btn btn-sm btn-primary" onClick={() => setShowAdd(true)}>
            <i className="bi bi-plus-lg me-1" />
            New key
          </button>
        )}
      </div>

      {revealed && (
        <div className="card-body border-bottom">
          <div className="alert alert-success mb-0">
            <div className="fw-semibold mb-1">
              <i className="bi bi-check-circle-fill me-1" />
              Key "{revealed.name}" is ready
            </div>
            <p className="small mb-2">
              Copy it now. Only a hash is stored, so this is the only time it can be shown.
            </p>
            <div className="input-group">
              <input className="form-control font-monospace" readOnly value={revealed.key} />
              <button
                className="btn btn-outline-secondary"
                onClick={() => {
                  navigator.clipboard?.writeText(revealed.key);
                  showSuccess('Copied to clipboard.');
                }}
              >
                <i className="bi bi-clipboard" />
              </button>
              <button className="btn btn-outline-secondary" onClick={() => setRevealed(null)}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="card-body p-0">
        {apiKeys.length === 0 ? (
          <div className="text-center text-body-secondary py-4">No API keys yet.</div>
        ) : (
          <div className="table-responsive">
            <table className="table table-dashboard table-hover align-middle mb-0">
              <thead>
                <tr>
                  <th className="ps-3">Name</th>
                  <th>Key</th>
                  <th>Environment</th>
                  <th>Created</th>
                  <th>Last used</th>
                  <th className="text-center">Status</th>
                  {canManage && <th />}
                </tr>
              </thead>
              <tbody>
                {apiKeys.map((key) => (
                  <tr key={key.id}>
                    <td className="ps-3 fw-medium">{key.name}</td>
                    <td className="font-monospace small text-body-secondary">{key.keyPrefix}…</td>
                    <td>
                      <span
                        className={`badge badge-status ${
                          key.environment === 'Production'
                            ? 'bg-success-subtle text-success border border-success-subtle'
                            : 'bg-secondary-subtle text-secondary border border-secondary-subtle'
                        }`}
                      >
                        {key.environment}
                      </span>
                    </td>
                    <td className="small text-body-secondary">{formatDate(key.createdAt)}</td>
                    <td className="small text-body-secondary">
                      {key.lastUsedAt ? formatShortDateTime(key.lastUsedAt) : 'Never'}
                    </td>
                    <td className="text-center">
                      <span
                        className={`badge badge-status ${
                          key.isActive
                            ? 'bg-success-subtle text-success border border-success-subtle'
                            : 'bg-danger-subtle text-danger border border-danger-subtle'
                        }`}
                      >
                        {key.isActive ? 'Active' : 'Revoked'}
                      </span>
                    </td>
                    {canManage && (
                      <td className="text-end pe-3">
                        <button
                          className="btn btn-sm btn-outline-secondary me-1"
                          onClick={() => handleRotate(key)}
                          disabled={busy}
                          title="Issue a new secret for this key"
                        >
                          <i className="bi bi-arrow-repeat" />
                        </button>
                        {key.isActive && (
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleRevoke(key)}
                            disabled={busy}
                            title="Revoke this key"
                          >
                            <i className="bi bi-slash-circle" />
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal show={showAdd} title="New API key" onClose={() => setShowAdd(false)}>
        <form onSubmit={handleCreate}>
          <div className="mb-3">
            <label htmlFor="keyName" className="form-label">
              Name
            </label>
            <input
              id="keyName"
              className="form-control"
              value={newKeyName}
              onChange={(e) => setNewKeyName(e.target.value)}
              placeholder="e.g. Production backend"
              required
            />
          </div>
          <div className="mb-3">
            <label htmlFor="keyEnv" className="form-label">
              Environment
            </label>
            <select
              id="keyEnv"
              className="form-select"
              value={newKeyEnv}
              onChange={(e) => setNewKeyEnv(e.target.value)}
            >
              <option value="Test">Test</option>
              <option value="Production" disabled={!liveKeysAllowed}>
                Production {liveKeysAllowed ? '' : '(live access required)'}
              </option>
            </select>
            {!liveKeysAllowed && (
              <div className="form-text">
                {accessStage === 'BetaEnded'
                  ? 'Beta Live has ended. Choose a paid plan to create live keys.'
                  : 'Sandbox accounts can only issue test keys. Request live access (reviewed within 24 hours) to enable live keys.'}
              </div>
            )}
          </div>
          <div className="d-flex justify-content-end gap-2">
            <button type="button" className="btn btn-secondary" onClick={() => setShowAdd(false)}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={busy}>
              {busy ? 'Creating…' : 'Create key'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
